import React from 'react';

/**
 * Пунктирная плашка-подсказка АВТОРУ в превью конструктора (и служебные
 * заглушки плеера: «картинка не загрузилась»). Это не рамка блока покупателя,
 * поэтому её оформление живёт здесь, а не в справочнике рамок блоков
 * (`blockBorder`): сторож WEB-1697 считает рамки числом в рендерерах и держит
 * их количество только убывающим — три одинаковые пунктирные плашки внутри
 * `PublicBlockRenderer.tsx` (превью редиректа, ошибка картинки, кнопка в
 * магазин без адреса — пачка 18) сведены сюда, чтобы следующая подсказка не
 * становилась четвёртой копией стиля.
 */
export interface AuthorHintBoxProps {
  children: React.ReactNode;
  testId?: string;
  /** Плотная плашка (одна строка) вместо стандартного отступа 16. */
  compact?: boolean;
  /** Переопределения поверх базового стиля (цвет текста, фон). */
  style?: React.CSSProperties;
}

export function AuthorHintBox({
  children,
  testId,
  compact = false,
  style,
}: AuthorHintBoxProps): React.ReactElement {
  return (
    <div
      data-testid={testId}
      style={{
        textAlign: 'center',
        padding: compact ? '10px 12px' : 16,
        marginBottom: 16,
        borderRadius: compact
          ? 'var(--border-radius-sm, 6px)'
          : 'var(--border-radius-md, 8px)',
        border: '1px dashed var(--color-border-primary, #d9d9d9)',
        fontSize: 13,
        // WEB-1911 (возврат приёмки 07.09), класс WEB-966. Плашка стоит на
        // ПОВЕРХНОСТИ воронки, а не на подложке кабинета: цвет обязан идти
        // через --color-text-on-surface, который тема воронки считает против
        // своей поверхности. Тематические переменные кабинета
        // (--color-text-tertiary/-secondary/-primary) здесь запрещены — у
        // тёмной темы кабинета они СВЕТЛЫЕ, и подсказка становилась белой на
        // светлой канве (проверяющий волны 11 намерил rgba(255,255,255,0.45)
        // и увидел пустой пунктирный прямоугольник). Запасное значение —
        // ОДНОУРОВНЕВОЕ: вложенный var() в инлайн-стиле jsdom отбрасывает
        // целиком (класс WEB-925), и правило стало бы непроверяемым.
        color: 'var(--color-text-on-surface, rgba(0, 0, 0, 0.65))',
        // Ревизия пачки 26 (Ф2), продолжение того же класса. Цвет выше
        // посчитан против ПОВЕРХНОСТИ (`palette.surface`), а своего фона у
        // плашки не было — она лежала на фоне СТРАНИЦЫ. Штатная палитра
        // «тёмный фон + светлая поверхность» (WEB-966) давала тёмный текст на
        // тёмной подложке: невидимо у другой половины авторов. Пара
        // «фон/текст» обязана быть из ОДНОГО слоя — так уже сделано у
        // заглушки картинки в PublicBlockRenderer. Запасное значение
        // одноуровневое по той же причине, что у цвета (класс WEB-925).
        background: 'var(--color-bg-surface, #fafafa)',
        ...style,
      }}
    >
      {children}
    </div>
  );
}
